/**
 * FixedIncomePage — Sprint 21 HEPHAESTUS surface.
 *
 * P13-compliant: summary strip → views → yield curve → duration → credit →
 * inflation → sovereign → PortfolioImpactPanel → TrackRecordPanel → themes.
 *
 * Reuses AssetCard, PortfolioImpactPanel, TrackRecordPanel, ConvictionSparkline.
 */

import { useState, useEffect } from 'react';
import { Hammer, TrendingUp, TrendingDown, Minus, Activity } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import AssetCard from '../components/AssetCard.jsx';
import ConvictionSparkline from '../components/ConvictionSparkline.jsx';
import PortfolioImpactPanel from '../components/PortfolioImpactPanel.jsx';
import TrackRecordPanel from '../components/TrackRecordPanel.jsx';
import { usePage } from '../contexts/PageContext.jsx';

const STANCE_COLORS = {
  short: '#ef4444', neutral: '#6b7280', long: '#10b981',
  safe: '#10b981', cautious: '#f59e0b', distressed: '#ef4444',
  benign: '#10b981', persistent: '#f59e0b', unanchored: '#ef4444',
  watchful: '#f59e0b', stressed: '#ef4444',
  bullish: '#10b981', bearish: '#ef4444',
};

function StanceBadge({ stance, conviction }) {
  const color = STANCE_COLORS[stance] || '#6b7280';
  return (
    <span className="flex items-center gap-1.5">
      <span className="text-xs font-mono font-bold uppercase px-2 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>
        {stance || 'n/a'}
      </span>
      {conviction != null && <span className="text-[10px] font-mono text-korinth-muted">{conviction}/10</span>}
    </span>
  );
}

function ViewCard({ title, view, loading }) {
  if (loading) return (
    <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
      <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">{title}</div>
      <div className="text-[10px] font-mono text-korinth-muted italic mt-1">Loading...</div>
    </div>
  );
  return (
    <div className="bg-korinth-surface border border-korinth-border rounded-lg p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider">{title}</div>
        {view && <StanceBadge stance={view.stance} conviction={view.conviction} />}
      </div>
      <div className="text-[11px] font-mono text-korinth-text leading-relaxed">
        {view?.reasoning || 'HEPHAESTUS hasn\'t emitted a view yet. Data must populate first (wait ~12 min for the first fetch cycle).'}
      </div>
    </div>
  );
}

function CurveRow({ label, value, unit = '%' }) {
  return (
    <div className="flex items-center justify-between px-2 py-1 bg-korinth-bg rounded text-[10px] font-mono">
      <span className="text-korinth-muted">{label}</span>
      <span className="text-white font-bold">{value != null ? `${value.toFixed(2)}${unit}` : '—'}</span>
    </div>
  );
}

function InstrumentGrid({ instruments, view, recs, onClickAsset }) {
  const filtered = instruments.filter(i => i.view === view);
  if (filtered.length === 0) {
    return <div className="text-[10px] text-korinth-muted font-mono italic py-3 text-center">No data for {view} instruments yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {filtered.map(i => {
        const rec = recs.find(r => r.asset === i.instrument);
        return (
          <AssetCard
            key={i.instrument}
            symbol={i.instrument}
            name={i.name}
            category={i.view}
            asset_type="fixed_income"
            price_usd={i.price}
            change_24h={i.change_1d}
            kairos_view={rec ? {
              recommendation: { action: rec.direction, conviction: rec.conviction_overall },
              predictions: [], hit_rate_30d: null,
            } : null}
            onClick={onClickAsset ? () => onClickAsset(i.instrument) : undefined}
          />
        );
      })}
    </div>
  );
}

export default function FixedIncomePage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('fixed-income'); }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impact, setImpact] = useState({ affected_tickers: [], impact_summary: null });
  const [track, setTrack] = useState({ metric_name: 'HEPHAESTUS', sample_size: 0, trajectory: [], recent_calls: [] });

  useEffect(() => {
    async function load() {
      setLoading(true);
      const json = async (url) => { try { const r = await fetch(url); return r.ok ? await r.json() : null; } catch { return null; } };
      const [overview, impactRes, trackRes] = await Promise.all([
        json('/api/fixed-income/overview'),
        json('/api/tracking/portfolio-impact/fixed-income'),
        json('/api/tracking/hephaestus'),
      ]);
      if (overview) setData(overview);
      if (impactRes) setImpact(impactRes);
      if (trackRes) setTrack(trackRes);
      setLoading(false);
    }
    load();
    const iv = setInterval(load, 120000);
    return () => clearInterval(iv);
  }, []);

  const views = data?.views || null;
  const instruments = data?.instruments || [];
  const curve = data?.curve;
  const spreads = data?.spreads || [];
  const recs = data?.active_recommendations || [];
  const preds = data?.active_predictions || [];
  const themes = data?.themes || [];
  const avgConviction = data?.avg_conviction;

  // Derive curve trajectory for sparkline
  const curveHistory = data?.curve_history || [];
  const spread2s10sTrajectory = curveHistory.map(h => ({ date: h.snapshot_at, value: h.spread_2s10s })).filter(d => d.value != null);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Hammer className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Fixed Income</h1>
        <span className="text-korinth-muted text-sm font-mono">HEPHAESTUS · Forges bond & credit theses</span>
      </div>

      {/* Summary strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Duration:</span> <span className="text-white font-bold">{views?.duration?.stance || '—'}</span></span>
        <span><span className="text-korinth-muted">Credit:</span> <span className="text-white font-bold">{views?.credit?.stance || '—'}</span></span>
        <span><span className="text-korinth-muted">Inflation:</span> <span className="text-white font-bold">{views?.inflation?.stance || '—'}</span></span>
        <span><span className="text-korinth-muted">Sovereign:</span> <span className="text-white font-bold">{views?.sovereign?.stance || '—'}</span></span>
        <span className="ml-auto"><span className="text-korinth-muted">Active bond recs:</span> <span className="text-korinth-accent font-bold">{recs.length}</span>{avgConviction != null && <span className="text-korinth-muted"> · avg {avgConviction.toFixed(1)}/10</span>}</span>
      </div>

      {/* HEPHAESTUS four views */}
      <ModuleContainer title="HEPHAESTUS · Current Views" subtitle="Updated every 2 hours">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <ViewCard title="Duration" view={views?.duration} loading={loading && !views} />
          <ViewCard title="Credit" view={views?.credit} loading={loading && !views} />
          <ViewCard title="Inflation" view={views?.inflation} loading={loading && !views} />
          <ViewCard title="Sovereign" view={views?.sovereign} loading={loading && !views} />
        </div>
      </ModuleContainer>

      {/* Yield curve */}
      <ModuleContainer title="Yield Curve" subtitle={curve ? `As of ${curve.snapshot_at?.slice(0, 16) || 'latest'}` : 'Awaiting first FRED fetch (~12 min)'}>
        {curve ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <CurveRow label="2Y" value={curve.yield_2y} />
              <CurveRow label="5Y" value={curve.yield_5y} />
              <CurveRow label="10Y" value={curve.yield_10y} />
              <CurveRow label="30Y" value={curve.yield_30y} />
            </div>
            <div className="space-y-1">
              <CurveRow label="2s10s spread" value={curve.spread_2s10s} />
              <CurveRow label="2s30s spread" value={curve.spread_2s30s} />
              <CurveRow label="10Y real (TIPS)" value={curve.real_10y} />
              <CurveRow label="10Y breakeven" value={curve.breakeven_10y} />
            </div>
            {spread2s10sTrajectory.length > 1 && (
              <div className="md:col-span-2">
                <div className="text-[9px] font-mono text-korinth-muted uppercase tracking-wider mb-1">2s10s trajectory (60d)</div>
                <ConvictionSparkline data={spread2s10sTrajectory} stance={curve.spread_2s10s >= 0 ? 'bullish' : 'bearish'} width={600} height={60} />
                {curve.spread_2s10s < 0 && <div className="text-[10px] font-mono text-korinth-warning mt-1">Curve inverted — historical recession precursor. HEPHAESTUS discounts this if growth data remains strong.</div>}
              </div>
            )}
          </div>
        ) : (
          <div className="text-[10px] font-mono text-korinth-muted italic py-4 text-center">
            Waiting for first yield curve snapshot. FRED_API_KEY required for full coverage; ensure it's set in backend/.env.
          </div>
        )}
      </ModuleContainer>

      {/* Duration view */}
      <ModuleContainer title="Duration" subtitle={views?.duration ? `${views.duration.stance?.toUpperCase()} bias · conviction ${views.duration.conviction}/10` : 'HEPHAESTUS view pending'}>
        <InstrumentGrid instruments={instruments} view="duration" recs={recs} />
      </ModuleContainer>

      {/* Credit view */}
      <ModuleContainer title="Credit" subtitle={views?.credit ? `${views.credit.stance?.toUpperCase()} posture · conviction ${views.credit.conviction}/10` : 'HEPHAESTUS view pending'}>
        {spreads.length > 0 && (
          <div className="mb-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            {spreads.map(s => (
              <div key={s.spread_type} className="bg-korinth-bg rounded px-3 py-2 text-[10px] font-mono">
                <div className="text-korinth-muted uppercase tracking-wider">{s.spread_type.replace(/_/g, '-')}</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg text-white font-bold">{s.spread_bps?.toFixed(1) ?? '—'}</span>
                  <span className="text-korinth-muted">bps</span>
                  {s.change_1d != null && (
                    <span className={s.change_1d >= 0 ? 'text-korinth-danger' : 'text-korinth-accent'}>
                      {s.change_1d >= 0 ? '+' : ''}{s.change_1d.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <InstrumentGrid instruments={instruments} view="credit" recs={recs} />
      </ModuleContainer>

      {/* Inflation view */}
      <ModuleContainer title="Inflation" subtitle={views?.inflation ? `${views.inflation.stance?.toUpperCase()} regime · conviction ${views.inflation.conviction}/10` : 'HEPHAESTUS view pending'}>
        <InstrumentGrid instruments={instruments} view="inflation" recs={recs} />
      </ModuleContainer>

      {/* Sovereign view */}
      <ModuleContainer title="Sovereign" subtitle={views?.sovereign ? `${views.sovereign.stance?.toUpperCase()} stance · conviction ${views.sovereign.conviction}/10` : 'HEPHAESTUS view pending'}>
        <InstrumentGrid instruments={instruments} view="sovereign" recs={recs} />
      </ModuleContainer>

      {/* Active predictions */}
      {preds.length > 0 && (
        <ModuleContainer title="Active Predictions" subtitle={`${preds.length} open fixed-income theses`}>
          <div className="space-y-1">
            {preds.map(p => (
              <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
                <span className="text-white font-bold w-14">{p.asset}</span>
                <span className={p.direction === 'bullish' ? 'text-korinth-accent' : 'text-korinth-danger'}>{p.direction === 'bullish' ? '▲' : '▼'}</span>
                <span className="text-korinth-text w-20">{p.timeframe}</span>
                <span className="text-korinth-accent font-bold">{p.confidence}%</span>
                <span className="text-korinth-muted flex-1 truncate">{p.thesis}</span>
              </div>
            ))}
          </div>
        </ModuleContainer>
      )}

      {/* Portfolio impact */}
      <PortfolioImpactPanel topic="Fixed Income" {...impact} loading={loading} />

      {/* Track record */}
      <TrackRecordPanel {...track} loading={loading} metric_name="HEPHAESTUS · Fixed Income" />

      {/* Themes cross-reference */}
      {themes.length > 0 && (
        <ModuleContainer title="Related Themes" subtitle="Themes touching bond/credit tickers">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {themes.map(t => {
              const color = STANCE_COLORS[t.current_stance] || '#6b7280';
              return (
                <a key={t.id} href="/themes" className="flex items-center gap-2 px-3 py-2 bg-korinth-bg rounded border border-korinth-border/30 hover:border-korinth-accent/40 transition-colors">
                  <span className="text-xs text-white font-bold">{t.name}</span>
                  <span className="text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 rounded" style={{ color, backgroundColor: color + '15' }}>{t.current_stance}</span>
                  <span className="text-[10px] font-mono text-korinth-accent ml-auto">{t.current_conviction?.toFixed(1)}/10</span>
                </a>
              );
            })}
          </div>
        </ModuleContainer>
      )}
    </div>
  );
}
